<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TeletelMetaStatusAPI from 'dashboard/api/teletel/metaStatus';
import PageHeader from 'dashboard/components-next/teletel/PageHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TemplateComposer from './TemplateComposer.vue';

const { t } = useI18n();

const tab = ref('status');
const tabs = ['status', 'notices', 'templates', 'settings'];
const loading = ref(true);
const checking = ref(false);
const error = ref('');
const numbers = ref([]);
const notices = ref([]);
const templates = ref([]);
const templatesLoaded = ref(false);
const syncing = ref(false);
const composing = ref(false);
const showResolved = ref(false);
const forbidden = ref(false);

// Settings tab (owner decision 2026-09-25): the account decides its checks
// and notifications; the page itself is granted from the super admin.
const prefs = ref({
  notifications: true,
  email: true,
  daily_check: true,
  recipients: [],
  scope: 'account',
});
const recipientsText = ref('');
const savingPrefs = ref(false);
const applyPrefs = data => {
  if (!data) return;
  prefs.value = { ...prefs.value, ...data };
  recipientsText.value = (data.recipients || []).join('\n');
};
const savePrefs = async () => {
  savingPrefs.value = true;
  try {
    const { data } = await TeletelMetaStatusAPI.updatePrefs({
      ...prefs.value,
      recipients: recipientsText.value
        .split(/[\n,;]+/)
        .map(v => v.trim())
        .filter(Boolean),
    });
    applyPrefs(data.prefs);
    useAlert(t('TELETEL_META.SETTINGS.SAVED'));
  } catch (e) {
    useAlert(t('TELETEL_META.LOAD_ERROR'));
  } finally {
    savingPrefs.value = false;
  }
};

const load = async () => {
  try {
    const { data } = await TeletelMetaStatusAPI.show();
    numbers.value = data.numbers || [];
    notices.value = data.notices || [];
    applyPrefs(data.prefs);
  } catch (e) {
    forbidden.value = e?.response?.status === 403;
    error.value = forbidden.value
      ? t('TELETEL_META.SETTINGS.DISABLED')
      : t('TELETEL_META.LOAD_ERROR');
  } finally {
    loading.value = false;
  }
};

const checkNow = async () => {
  checking.value = true;
  try {
    const { data } = await TeletelMetaStatusAPI.check();
    numbers.value = data.numbers || [];
    notices.value = data.notices || [];
    useAlert(t('TELETEL_META.STATUS.CHECKED'));
  } catch (e) {
    useAlert(t('TELETEL_META.LOAD_ERROR'));
  } finally {
    checking.value = false;
  }
};

const loadTemplates = async () => {
  try {
    const { data } = await TeletelMetaStatusAPI.templates();
    templates.value = data.templates || [];
    templatesLoaded.value = true;
  } catch (e) {
    useAlert(t('TELETEL_META.LOAD_ERROR'));
  }
};

const onTemplateCreated = async () => {
  composing.value = false;
  await loadTemplates();
};

const syncTemplates = async inboxId => {
  syncing.value = true;
  try {
    await TeletelMetaStatusAPI.syncTemplates(inboxId);
    await loadTemplates();
    useAlert(t('TELETEL_META.TEMPLATES.SYNCED'));
  } catch (e) {
    useAlert(t('TELETEL_META.LOAD_ERROR'));
  } finally {
    syncing.value = false;
  }
};

const selectTab = name => {
  tab.value = name;
  if (name === 'templates' && !templatesLoaded.value) loadTemplates();
};

const toggleResolved = async notice => {
  try {
    const { data } = await TeletelMetaStatusAPI.resolveNotice(
      notice.id,
      !notice.resolved_at
    );
    notices.value = data.notices || [];
  } catch (e) {
    useAlert(t('TELETEL_META.LOAD_ERROR'));
  }
};

const visibleNotices = computed(() =>
  showResolved.value ? notices.value : notices.value.filter(n => !n.resolved_at)
);

const openAccountNotices = computed(() =>
  notices.value.filter(
    n => !n.resolved_at && ['account', 'token'].includes(n.kind)
  )
);

// Meta's words → ours. Unknown values fall back to the raw value.
const label = (group, value) => {
  if (value === undefined || value === null || value === '') return '—';
  const key = `TELETEL_META.VALUES.${group}.${String(value).toUpperCase()}`;
  const translated = t(key);
  return translated === key ? String(value) : translated;
};

const verdictClass = verdict => {
  if (verdict === 'AVAILABLE') return 'bg-n-teal-3 text-n-teal-11';
  if (verdict === 'LIMITED') return 'bg-n-amber-3 text-n-amber-11';
  if (verdict === 'BLOCKED') return 'bg-n-ruby-3 text-n-ruby-11';
  return 'bg-n-slate-3 text-n-slate-11';
};

const qualityClass = quality => {
  if (quality === 'GREEN') return 'text-n-teal-11';
  if (quality === 'YELLOW') return 'text-n-amber-11';
  if (quality === 'RED') return 'text-n-ruby-11';
  return 'text-n-slate-11';
};

const statusClass = status => {
  if (status === 'APPROVED') return 'bg-n-teal-3 text-n-teal-11';
  if (status === 'PENDING') return 'bg-n-amber-3 text-n-amber-11';
  if (['REJECTED', 'PAUSED', 'DISABLED'].includes(status))
    return 'bg-n-ruby-3 text-n-ruby-11';
  return 'bg-n-slate-3 text-n-slate-11';
};

const formatTime = value => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

onMounted(load);
</script>

<template>
  <div class="flex w-full flex-col gap-6">
    <PageHeader
      :title="t('TELETEL_META.TITLE')"
      :description="t('TELETEL_META.DESCRIPTION')"
    >
      <template #actions>
        <Button
          :label="t('TELETEL_META.STATUS.CHECK_NOW')"
          icon="i-lucide-refresh-cw"
          size="sm"
          :is-loading="checking"
          @click="checkNow"
        />
      </template>
    </PageHeader>

    <div
      v-if="openAccountNotices.length"
      class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 px-4 py-3 text-sm text-n-ruby-12"
    >
      <p class="mb-1 font-semibold">{{ t('TELETEL_META.BLOCKED_BANNER') }}</p>
      <ul class="list-disc ps-5">
        <li v-for="n in openAccountNotices" :key="n.id">
          {{ n.title || n.meta_title }} — {{ n.fix }}
        </li>
      </ul>
    </div>

    <div class="flex gap-2 border-b border-n-weak">
      <button
        v-for="name in tabs"
        :key="name"
        type="button"
        class="-mb-px border-b-2 px-3 py-2 text-sm"
        :class="
          tab === name
            ? 'border-n-brand text-n-slate-12 font-medium'
            : 'border-transparent text-n-slate-11'
        "
        @click="selectTab(name)"
      >
        {{ t(`TELETEL_META.TABS.${name.toUpperCase()}`) }}
        <span
          v-if="name === 'notices' && visibleNotices.length"
          class="ms-1 rounded-full bg-n-slate-4 px-1.5 text-xs"
        >
          {{ visibleNotices.length }}
        </span>
      </button>
    </div>

    <div v-if="loading" class="py-8 text-center text-sm text-n-slate-11">
      {{ t('TELETEL_META.LOADING') }}
    </div>
    <div v-else-if="error" class="py-8 text-center text-sm text-n-ruby-11">
      {{ error }}
    </div>

    <!-- Status -->
    <div v-else-if="tab === 'status'" class="flex flex-col gap-4">
      <p v-if="!numbers.length" class="text-sm text-n-slate-11">
        {{ t('TELETEL_META.STATUS.NO_NUMBERS') }}
      </p>
      <div
        v-for="n in numbers"
        :key="n.channel_id"
        class="rounded-lg border border-n-weak bg-n-solid-1 p-4"
      >
        <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ n.inbox_name }}
              <span class="text-sm font-normal text-n-slate-11" dir="ltr">
                {{ n.phone_number }}
              </span>
            </h3>
            <p class="text-xs text-n-slate-11">
              {{ t('TELETEL_META.STATUS.CHECKED_AT') }}:
              {{ formatTime(n.checked_at) }}
            </p>
          </div>
          <span
            class="rounded-full px-3 py-1 text-sm font-medium"
            :class="verdictClass(n.can_send)"
          >
            {{ label('CAN_SEND', n.can_send) }}
          </span>
        </div>

        <p v-if="!n.checked_at" class="text-sm text-n-slate-11">
          {{ t('TELETEL_META.STATUS.NEVER_CHECKED') }}
        </p>
        <div
          v-else
          class="grid grid-cols-1 gap-x-8 gap-y-2 text-sm md:grid-cols-2"
        >
          <div class="flex justify-between gap-4 border-b border-n-weak py-1">
            <span class="text-n-slate-11">{{
              t('TELETEL_META.STATUS.QUALITY')
            }}</span>
            <span class="font-medium" :class="qualityClass(n.phone?.quality)">
              {{ label('QUALITY', n.phone?.quality) }}
            </span>
          </div>
          <div class="flex justify-between gap-4 border-b border-n-weak py-1">
            <span class="text-n-slate-11">{{
              t('TELETEL_META.STATUS.NAME_STATUS')
            }}</span>
            <span class="font-medium">
              {{ label('NAME_STATUS', n.phone?.name_status) }}
              <span class="text-n-slate-11">
                {{ n.phone?.verified_name }}
              </span>
            </span>
          </div>
          <div class="flex justify-between gap-4 border-b border-n-weak py-1">
            <span class="text-n-slate-11">{{
              t('TELETEL_META.STATUS.OFFICIAL')
            }}</span>
            <span class="font-medium">{{
              n.phone?.official ? t('TELETEL_META.YES') : t('TELETEL_META.NO')
            }}</span>
          </div>
          <div class="flex justify-between gap-4 border-b border-n-weak py-1">
            <span class="text-n-slate-11">{{
              t('TELETEL_META.STATUS.REVIEW')
            }}</span>
            <span class="font-medium">{{
              label('REVIEW', n.waba?.review)
            }}</span>
          </div>
          <div class="flex justify-between gap-4 border-b border-n-weak py-1">
            <span class="text-n-slate-11">{{
              t('TELETEL_META.STATUS.VERIFICATION')
            }}</span>
            <span class="font-medium">{{
              label('VERIFICATION', n.waba?.verification)
            }}</span>
          </div>
          <div class="flex justify-between gap-4 border-b border-n-weak py-1">
            <span class="text-n-slate-11">{{
              t('TELETEL_META.STATUS.CURRENCY')
            }}</span>
            <span
              class="font-medium"
              :class="n.waba?.currency ? '' : 'text-n-ruby-11'"
            >
              {{ n.waba?.currency || t('TELETEL_META.STATUS.NO_CURRENCY') }}
            </span>
          </div>
          <div class="flex justify-between gap-4 border-b border-n-weak py-1">
            <span class="text-n-slate-11">{{
              t('TELETEL_META.STATUS.WABA')
            }}</span>
            <span class="font-medium">
              {{ n.waba?.name }}
              <span class="text-n-slate-11" dir="ltr">{{ n.waba?.id }}</span>
            </span>
          </div>
        </div>

        <div v-if="n.issues?.length" class="mt-4 flex flex-col gap-2">
          <div
            v-for="issue in n.issues"
            :key="`${issue.code}-${issue.entity}`"
            class="rounded-md px-3 py-2 text-sm"
            :class="
              issue.severity === 'blocked'
                ? 'bg-n-ruby-2 text-n-ruby-12'
                : 'bg-n-amber-2 text-n-amber-12'
            "
          >
            <p class="font-semibold">
              {{ issue.title || issue.meta_title }}
              <span class="text-xs font-normal opacity-70" dir="ltr">
                {{ issue.code }} · {{ issue.entity }}
              </span>
            </p>
            <p v-if="issue.fix">{{ t('TELETEL_META.FIX') }}: {{ issue.fix }}</p>
            <p
              v-if="issue.title && issue.meta_title"
              class="text-xs opacity-70"
              dir="ltr"
            >
              {{ issue.meta_title }}
            </p>
          </div>
        </div>
        <p v-else-if="n.checked_at" class="mt-3 text-sm text-n-teal-11">
          {{ t('TELETEL_META.STATUS.ALL_GOOD') }}
        </p>
      </div>
      <p class="text-xs text-n-slate-11">
        {{ t('TELETEL_META.STATUS.DAILY_NOTE') }}
      </p>
    </div>

    <!-- Notices -->
    <div v-else-if="tab === 'notices'" class="flex flex-col gap-3">
      <label class="flex items-center gap-2 text-sm text-n-slate-11">
        <input v-model="showResolved" type="checkbox" class="mb-0" />
        {{ t('TELETEL_META.NOTICES.SHOW_RESOLVED') }}
      </label>
      <p v-if="!visibleNotices.length" class="text-sm text-n-slate-11">
        {{ t('TELETEL_META.NOTICES.EMPTY') }}
      </p>
      <div
        v-for="n in visibleNotices"
        :key="n.id"
        class="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-n-weak p-3 text-sm"
        :class="n.resolved_at ? 'opacity-60' : ''"
      >
        <div class="min-w-0 flex-1">
          <p class="font-semibold text-n-slate-12">
            <span
              class="me-2 rounded px-1.5 py-0.5 text-xs"
              :class="
                ['account', 'token'].includes(n.kind)
                  ? 'bg-n-ruby-3 text-n-ruby-11'
                  : 'bg-n-slate-3 text-n-slate-11'
              "
            >
              {{ t(`TELETEL_META.KINDS.${n.kind.toUpperCase()}`) }}
            </span>
            {{ n.title || n.meta_title }}
          </p>
          <p v-if="n.fix" class="text-n-slate-12">
            {{ t('TELETEL_META.FIX') }}: {{ n.fix }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{ formatTime(n.last_at || n.at) }}
            · {{ t('TELETEL_META.NOTICES.INBOX') }}
            {{ n.inbox_name || n.inbox_id }} ·
            <span dir="ltr">{{ n.code }}</span>
            <span v-if="n.count > 1">
              · {{ t('TELETEL_META.NOTICES.TIMES', { count: n.count }) }}
            </span>
            <span v-if="n.template"> · {{ n.template }}</span>
            · {{ n.source }}
          </p>
          <p
            v-if="n.title && n.meta_title"
            class="text-xs text-n-slate-11"
            dir="ltr"
          >
            {{ n.meta_title }}
          </p>
          <p v-if="n.details" class="text-xs text-n-slate-11">
            {{ n.details }}
          </p>
        </div>
        <Button
          :label="
            n.resolved_at
              ? t('TELETEL_META.NOTICES.REOPEN')
              : t('TELETEL_META.NOTICES.RESOLVE')
          "
          size="sm"
          color="slate"
          variant="faded"
          @click="toggleResolved(n)"
        />
      </div>
    </div>

    <!-- Settings -->
    <div v-else-if="tab === 'settings'" class="flex max-w-2xl flex-col gap-5">
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('TELETEL_META.SETTINGS.TITLE') }}
      </p>
      <label
        v-for="key in ['daily_check', 'notifications', 'email']"
        :key="key"
        class="flex items-start gap-3 rounded-lg border border-n-weak p-3"
      >
        <input v-model="prefs[key]" type="checkbox" class="mb-0 mt-1" />
        <span>
          <span class="block text-sm font-medium text-n-slate-12">
            {{ t(`TELETEL_META.SETTINGS.${key.toUpperCase()}`) }}
          </span>
          <span class="block text-xs text-n-slate-11">
            {{ t(`TELETEL_META.SETTINGS.${key.toUpperCase()}_HINT`) }}
          </span>
        </span>
      </label>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TELETEL_META.SETTINGS.SCOPE') }}
        </label>
        <select v-model="prefs.scope" class="mb-0 text-sm">
          <option value="account">
            {{ t('TELETEL_META.SETTINGS.SCOPE_ACCOUNT') }}
          </option>
          <option value="all">
            {{ t('TELETEL_META.SETTINGS.SCOPE_ALL') }}
          </option>
        </select>
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TELETEL_META.SETTINGS.RECIPIENTS') }}
        </label>
        <textarea
          v-model="recipientsText"
          rows="3"
          dir="ltr"
          class="mb-0 text-sm"
        />
        <p class="text-xs text-n-slate-11">
          {{ t('TELETEL_META.SETTINGS.RECIPIENTS_HINT') }}
        </p>
      </div>
      <Button
        :label="t('TELETEL_META.SETTINGS.SAVE')"
        :is-loading="savingPrefs"
        class="w-fit"
        @click="savePrefs"
      />
    </div>

    <!-- Templates -->
    <div v-else class="flex flex-col gap-3">
      <TemplateComposer
        v-if="composing"
        :numbers="numbers"
        @created="onTemplateCreated"
        @close="composing = false"
      />
      <div class="flex flex-wrap items-center gap-2">
        <Button
          v-if="!composing && numbers.length"
          :label="t('TELETEL_META.COMPOSER.NEW')"
          icon="i-lucide-plus"
          size="sm"
          @click="composing = true"
        />
        <Button
          v-for="n in numbers"
          :key="n.channel_id"
          :label="`${t('TELETEL_META.TEMPLATES.SYNC')} — ${n.inbox_name}`"
          icon="i-lucide-refresh-cw"
          size="sm"
          color="slate"
          variant="faded"
          :is-loading="syncing"
          @click="syncTemplates(n.inbox_id)"
        />
      </div>
      <p
        v-if="templatesLoaded && !templates.length"
        class="text-sm text-n-slate-11"
      >
        {{ t('TELETEL_META.TEMPLATES.EMPTY') }}
      </p>
      <div
        v-for="tpl in templates"
        :key="`${tpl.inbox_id}-${tpl.id}`"
        class="rounded-lg border border-n-weak p-3 text-sm"
      >
        <div class="mb-1 flex flex-wrap items-center gap-2">
          <span class="font-semibold text-n-slate-12" dir="ltr">{{
            tpl.name
          }}</span>
          <span class="text-xs text-n-slate-11">{{ tpl.language }}</span>
          <span
            class="rounded px-1.5 py-0.5 text-xs"
            :class="statusClass(tpl.status)"
          >
            {{ label('TEMPLATE_STATUS', tpl.status) }}
          </span>
          <span
            class="rounded bg-n-slate-3 px-1.5 py-0.5 text-xs text-n-slate-11"
          >
            {{ label('CATEGORY', tpl.category) }}
          </span>
          <span
            v-if="tpl.header_format && tpl.header_format !== 'TEXT'"
            class="rounded bg-n-amber-3 px-1.5 py-0.5 text-xs text-n-amber-11"
          >
            {{ t('TELETEL_META.TEMPLATES.MEDIA_HEADER') }}:
            {{ label('HEADER', tpl.header_format) }}
          </span>
          <span class="ms-auto text-xs text-n-slate-11">{{
            tpl.inbox_name
          }}</span>
        </div>
        <p class="whitespace-pre-wrap text-n-slate-12">{{ tpl.body }}</p>
        <p
          v-if="tpl.rejected_reason && tpl.rejected_reason !== 'NONE'"
          class="mt-1 text-xs text-n-ruby-11"
        >
          {{ t('TELETEL_META.TEMPLATES.REJECTED') }}: {{ tpl.rejected_reason }}
        </p>
      </div>
      <p v-if="templates.length" class="text-xs text-n-slate-11">
        {{ t('TELETEL_META.TEMPLATES.SYNCED_AT') }}:
        {{ formatTime(templates[0].synced_at) }}
      </p>
    </div>
  </div>
</template>
